/* eslint-disable no-param-reassign */
import { ElementRef, inject, Injectable, Signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { first, Observable } from 'rxjs';

import { ImageDataMedia, ProductViewConfig, ViewModelProductView } from '../models/product-view-config.model';

import { GridElemType } from '@core/domain/types/product.types';
import { getRedirectToWorldWide } from '@core/infrastructure/state/core.selectors';
import { ProductPublicModels } from '@features/products/product';
import { ProductColor } from '@features/products/product/domain/models/product.models';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { ViewItemService } from '@shared/analytics/COMMON/services/product-view/view-item.service';
import { BundleClickAnalytics, ProductClickAnalytics, RelatedProductClickAnalytics, SeleccionarTallaAnalytics } from '@shared/analytics/store/app-events-analytics.actions';
import { redirectToWorldWideUseCase } from '@shared/product/services/product-utils.service';
import { itemOutOfStockUseCase } from '@shared/product/use-cases/product-size.use-cases';
import { AddProductsShopcartCore } from '@shared/shopcart-core/infrastructure/state/shopcart-core.actions';

function setImageData(hasVideo: boolean, product: ProductPublicModels.PublicProduct, imageType): ImageDataMedia {
    const imageData: ImageDataMedia = {
        type: null,
        gridElementType: null,
        ref: null,
    };
    if (imageType) {
        imageData.type = imageType;
    } else {
        imageData.type = hasVideo ? 'video' : 'image';
    }
    imageData.gridElementType = `_${product.gridElemType.toLowerCase()}`;
    switch (product.gridElemType) {
        case GridElemType.ESTILISMO:
            imageData.ref = product.xmediaDefaultSet !== null ? `_${product.xmediaDefaultSet.toString()}` : '';
            break;
        case GridElemType.BUNDLE:
            imageData.ref = `_${product.reference}`;
            break;
        default:
            imageData.ref = '';
            break;
    }
    return imageData;
}

@Injectable()
export class ProductViewService {
    // #region DEPENDENCIES
    private readonly store: Store = inject(Store);

    private readonly viewItemService: ViewItemService = inject(ViewItemService);
    // #endregion DEPENDENCIES

    private readonly redirectToWorldWide$$: Signal<string> = this.store.selectSignal(getRedirectToWorldWide);

    // pasar esto a un caso de uso de product
    public actionAddToCartProductView(
        product: ProductPublicModels.PublicProduct & { idMedia?: string },
        config: ProductViewConfig,
        categoryId: string
    ) {
        this.trackSizeSelection(product, config);

        if (this.redirectToWorldWide$$()) {
            this.redirectToWorldwide(product, categoryId);
            return;
        }

        this.store.dispatch(AddProductsShopcartCore({
            eventFrom: config.eventFrom,
            location: config.location,
            openPopup: true,
            parentType: config.parentType,
            place: config.place,
            products: [product],
            relatedsSectionId: config.relatedsSectionId,
        }));
    }

    /** Analytics */
    public actionWhenVisible(
        productViewViewModel$: Observable<ViewModelProductView>,
        elementRef: ElementRef,
        intersectionEntry: IntersectionObserverEntry,
    ) {
        productViewViewModel$.pipe(
            first((productViewViewModel) => !!productViewViewModel?.product)
        ).subscribe(({ product, templateConfig, medias }) => {
            let eventFrom: EventFrom = EventFrom.parrilla;

            if (templateConfig?.relatedsSectionId) {
                eventFrom = EventFrom.relateds;
            } else if (templateConfig?.place === 'SEARCH') {
                eventFrom = EventFrom.searchResults;
            }

            if (templateConfig.clickable) {
                this.viewItemService.actionWhenVisibleAsync(
                    product,
                    elementRef.nativeElement,
                    { place: templateConfig?.place, eventFrom, relatedsSectionId: templateConfig?.relatedsSectionId },
                    medias.image?.idMedia,
                    intersectionEntry,
                );
            }
        });
    }

    public productAnalytics(
        product: ProductPublicModels.PublicProduct,
        config: ProductViewConfig,
        hasVideo: boolean,
    ): void {
        const tagging = product?.tagging;

        if (config.eventFrom === EventFrom.relateds) {
            this.store.dispatch(RelatedProductClickAnalytics({
                baseProduct: config.baseProduct,
                parentType: config.parentType,
                place: config.place,
                product: { ...product, idMedia: config.media?.idMedia },
                relatedsSectionId: config.relatedsSectionId,
            }));
            if (typeof hj !== 'undefined') {
                hj('tagRecording', ['relacionados']);
            }
        } else {
            if (hasVideo) {
                const imageData: ImageDataMedia = setImageData(true, product, config);
                // biome-ignore lint/style/noParameterAssign: <explanation>
                product = { ...product, imageData };
                if (product.bundleProducts?.length) {
                    product.bundleProducts = product.bundleProducts.map(
                        (bundleProduct: ProductPublicModels.PublicProduct) => {
                            return { ...bundleProduct, imageData };
                        }
                    );
                }
            }
            if (product?.gridElemType?.toUpperCase() === GridElemType.BUNDLE) {
                this.store.dispatch(BundleClickAnalytics({
                    baseProduct: config.baseProduct,
                    eventFrom: EventFrom.parrilla,
                    parentType: config.parentType,
                    place: config.place,
                    product: { ...product, idMedia: config.media?.idMedia },
                }));
            } else {
                this.store.dispatch(ProductClickAnalytics({
                    place: config.place,
                    product: { ...product, idMedia: config.media?.idMedia },
                    tagging,
                    eventFrom: {
                        ficha_bundle: EventFrom.fichaBundle,
                        related_product: EventFrom.relateds,
                        search: EventFrom.searchResults,
                    }?.[config.eventFrom] || EventFrom.parrilla,
                }));
            }
        }
    }

    public setDefinedColor(
        product: ProductPublicModels.PublicProduct,
        config: ProductViewConfig
    ): ProductColor {
        let definedColor: ProductColor;
        const configColorId = config?.baseColor;
        if (config?.location === 'related_product' && configColorId) {
            const haveStyle = !!configColorId.split('&styleId=')[1];
            const colorId = haveStyle ? configColorId.split('&styleId=')[0] : configColorId;
            definedColor = product.colors?.find((col) => col.id === colorId);
        }
        return definedColor;
    }

    // #region PRIVATE METHODS
    private trackSizeSelection(
        product: ProductPublicModels.PublicProduct & { idMedia?: string },
        config: ProductViewConfig
    ): void {
        this.store.dispatch(SeleccionarTallaAnalytics({
            product: { ...product },
            config,
        }));
    }

    private redirectToWorldwide(
        product: ProductPublicModels.PublicProduct,
        categoryId: string
    ): void {
        const availableFlag = itemOutOfStockUseCase(product, product.status.selectedSize);
        redirectToWorldWideUseCase(this.store, availableFlag, product, categoryId);
    }
    // #endregion PRIVATE METHODS
}
